// src/stores/workflowStore.js
import { create } from 'zustand';
import * as workflowService from '../services/workflowService';
import useUIStore from './uiStore';

const useWorkflowStore = create((set, get) => ({
  // Template state
  templates: [],
  currentTemplate: null,
  
  // Instance state
  instances: [],
  currentInstance: null,
  pendingWorkflows: [],
  clientWorkflows: [],
  
  // Loading and error states
  isLoading: false,
  criticalError: null,    // For blocking errors (e.g., failed to load workflow list)
  operationError: null,   // For non-blocking errors (e.g., failed to update workflow)
  
  // Clear errors
  clearError: () => set({ criticalError: null, operationError: null }),
  clearCriticalError: () => set({ criticalError: null }),
  clearOperationError: () => set({ operationError: null }),
  
  // Template actions
  fetchTemplates: async (params = {}) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await workflowService.getWorkflowTemplates(params);
      set({ 
        templates: response.data.templates || [],
        isLoading: false
      });
      return response.data;
    } catch (error) {
      set({ 
        isLoading: false,
        criticalError: error.message || 'Failed to fetch workflow templates' 
      });
      console.log('error', error);
      // Don't throw - return empty data
      return { templates: [] };
    }
  },
  
  fetchTemplateById: async (id) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await workflowService.getWorkflowTemplateById(id);
      set({ 
        currentTemplate: response.data.template || null,
        isLoading: false
      });
      return response.data.template;
    } catch (error) {
      set({ 
        isLoading: false,
        criticalError: error.message || 'Failed to fetch workflow template' 
      });
      console.log('error', error);
      // Don't throw - return null
      return null;
    }
  },
  
  createTemplate: async (templateData) => {
    set({ isLoading: true, operationError: null });
    try {
      const response = await workflowService.createWorkflowTemplate(templateData);
      set(state => ({ 
        templates: [...state.templates, response.data.template],
        isLoading: false
      }));
      return response.data.template;
    } catch (error) {
      const errorMessage = error.message || 'Failed to create workflow template';
      set({ 
        isLoading: false,
        operationError: errorMessage 
      });
      // Show notification
      useUIStore.getState().addNotification({ message: errorMessage, type: 'error' });
      console.log('error', error);
      throw error;
    }
  },
  
  updateTemplate: async (id, templateData) => {
    set({ isLoading: true, operationError: null });
    try {
      const response = await workflowService.updateWorkflowTemplate(id, templateData);
      set(state => ({ 
        templates: state.templates.map(template => 
          template._id === id ? response.data.template : template
        ),
        currentTemplate: response.data.template,
        isLoading: false
      }));
      return response.data.template;
    } catch (error) {
      const errorMessage = error.message || 'Failed to update workflow template';
      set({ 
        isLoading: false,
        operationError: errorMessage 
      });
      // Show notification
      useUIStore.getState().addNotification({ message: errorMessage, type: 'error' });
      console.log('error', error);
      throw error;
    }
  },
  
  deleteTemplate: async (id) => {
    set({ isLoading: true, operationError: null });
    try {
      await workflowService.deleteWorkflowTemplate(id);
      set(state => ({ 
        templates: state.templates.filter(template => template._id !== id),
        isLoading: false
      }));
      return true;
    } catch (error) {
      const errorMessage = error.message || 'Failed to delete workflow template';
      set({ 
        isLoading: false,
        operationError: errorMessage 
      });
      // Show notification
      useUIStore.getState().addNotification({ message: errorMessage, type: 'error' });
      console.log('error', error);
      throw error;
    }
  },
  
  // Workflow instance actions
  fetchInstances: async (params = {}) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await workflowService.getWorkflowInstances(params);
      set({ 
        instances: response.data.instances || [],
        isLoading: false
      });
      return response.data;
    } catch (error) {
      set({ 
        isLoading: false,
        criticalError: error.message || 'Failed to fetch workflow instances' 
      });
      console.log('error', error);
      // Don't throw - return empty data
      return { instances: [] };
    }
  },
  
  fetchInstanceById: async (id) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await workflowService.getWorkflowInstanceById(id);
      set({ 
        currentInstance: response.data.instance || null,
        isLoading: false
      });
      return response.data.instance;
    } catch (error) {
      set({ 
        isLoading: false,
        criticalError: error.message || 'Failed to fetch workflow instance' 
      });
      console.log('error', error);
      // Don't throw - return null
      return null;
    }
  },
  
  createInstance: async (instanceData) => {
    set({ isLoading: true, operationError: null });
    try {
      const response = await workflowService.createWorkflowInstance(instanceData);
      set(state => ({ 
        instances: [...state.instances, response.data.instance],
        isLoading: false
      }));
      return response.data.instance;
    } catch (error) {
      const errorMessage = error.message || 'Failed to create workflow instance';
      set({ 
        isLoading: false,
        operationError: errorMessage 
      });
      // Show notification
      useUIStore.getState().addNotification({ message: errorMessage, type: 'error' });
      console.log('error', error);
      throw error;
    }
  },
  
  // Workflow actions
  advanceWorkflow: async (id, data = {}) => {
    set({ isLoading: true, operationError: null });
    try {
      const response = await workflowService.advanceWorkflow(id, data);
      // Update the instances list and current instance if it matches
      set(state => {
        const updatedInstances = state.instances.map(instance => 
          instance._id === id ? response.data.instance : instance
        );
        return {
          instances: updatedInstances,
          currentInstance: state.currentInstance?._id === id 
            ? response.data.instance 
            : state.currentInstance,
          pendingWorkflows: state.pendingWorkflows.filter(wf => wf._id !== id),
          isLoading: false
        };
      });
      return response.data.instance;
    } catch (error) {
      const errorMessage = error.message || 'Failed to advance workflow';
      set({ 
        isLoading: false,
        operationError: errorMessage 
      });
      // Show notification
      useUIStore.getState().addNotification({ message: errorMessage, type: 'error' });
      console.log('error', error);
      throw error;
    }
  },
  
  rejectWorkflow: async (id, data = {}) => {
    set({ isLoading: true, operationError: null });
    try {
      const response = await workflowService.rejectWorkflow(id, data);
      // Update the instances list and current instance if it matches
      set(state => {
        const updatedInstances = state.instances.map(instance => 
          instance._id === id ? response.data.instance : instance
        );
        return {
          instances: updatedInstances,
          currentInstance: state.currentInstance?._id === id 
            ? response.data.instance 
            : state.currentInstance,
          pendingWorkflows: state.pendingWorkflows.filter(wf => wf._id !== id),
          isLoading: false
        };
      });
      return response.data.instance;
    } catch (error) {
      const errorMessage = error.message || 'Failed to reject workflow';
      set({ 
        isLoading: false,
        operationError: errorMessage 
      });
      // Show notification
      useUIStore.getState().addNotification({ message: errorMessage, type: 'error' });
      console.log('error', error);
      throw error;
    }
  },
  
  skipWorkflowStep: async (id, data = {}) => {
    set({ isLoading: true, operationError: null });
    try {
      const response = await workflowService.skipWorkflowStep(id, data);
      // Update the instances list and current instance if it matches
      set(state => ({
        instances: state.instances.map(instance => 
          instance._id === id ? response.data.instance : instance
        ),
        currentInstance: state.currentInstance?._id === id 
          ? response.data.instance 
          : state.currentInstance,
        isLoading: false
      }));
      return response.data.instance;
    } catch (error) {
      const errorMessage = error.message || 'Failed to skip workflow step';
      set({ 
        isLoading: false,
        operationError: errorMessage 
      });
      // Show notification
      useUIStore.getState().addNotification({ message: errorMessage, type: 'error' });
      console.log('error', error);
      throw error;
    }
  },
  
  cancelWorkflow: async (id, data = {}) => {
    set({ isLoading: true, operationError: null });
    try {
      const response = await workflowService.cancelWorkflow(id, data);
      // Update the instances list and current instance if it matches
      set(state => ({
        instances: state.instances.map(instance => 
          instance._id === id ? response.data.instance : instance
        ),
        currentInstance: state.currentInstance?._id === id 
          ? response.data.instance 
          : state.currentInstance,
        pendingWorkflows: state.pendingWorkflows.filter(wf => wf._id !== id),
        isLoading: false
      }));
      return response.data.instance;
    } catch (error) {
      const errorMessage = error.message || 'Failed to cancel workflow';
      set({ 
        isLoading: false,
        operationError: errorMessage 
      });
      // Show notification
      useUIStore.getState().addNotification({ message: errorMessage, type: 'error' });
      console.log('error', error);
      throw error;
    }
  },
  
  restartWorkflow: async (id, data = {}) => {
    set({ isLoading: true, operationError: null });
    try {
      const response = await workflowService.restartWorkflow(id, data);
      // Update the instances list and current instance if it matches
      set(state => ({
        instances: state.instances.map(instance => 
          instance._id === id ? response.data.instance : instance
        ),
        currentInstance: state.currentInstance?._id === id 
          ? response.data.instance 
          : state.currentInstance,
        isLoading: false
      }));
      return response.data.instance;
    } catch (error) {
      const errorMessage = error.message || 'Failed to restart workflow';
      set({ 
        isLoading: false,
        operationError: errorMessage 
      });
      // Show notification
      useUIStore.getState().addNotification({ message: errorMessage, type: 'error' });
      console.log('error', error);
      throw error;
    }
  },
  
  // Fetch pending workflows for current user
  fetchMyPendingWorkflows: async () => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await workflowService.getMyPendingWorkflows();
      set({ 
        pendingWorkflows: response.data.workflows || [],
        isLoading: false
      });
      return response.data.workflows;
    } catch (error) {
      set({ 
        isLoading: false,
        criticalError: error.message || 'Failed to fetch pending workflows' 
      });
      console.log('error', error);
      // Don't throw - return empty array
      return [];
    }
  },
  
  // Fetch workflows for a client
  fetchClientWorkflows: async (clientId) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await workflowService.getClientWorkflows(clientId);
      set({ 
        clientWorkflows: response.data.workflows || [],
        isLoading: false
      });
      return response.data.workflows;
    } catch (error) {
      set({ 
        isLoading: false,
        criticalError: error.message || 'Failed to fetch client workflows' 
      });
      console.log('error', error);
      // Don't throw - return empty array
      return [];
    }
  }
}));

export default useWorkflowStore;